import React from "react";
import { Link } from "react-router-dom";
import { TEAM } from "../lib/content";
import SEO from "../components/SEO";
import CEOMessage from "../components/hidroverse/CEOMessage";

// Cropped portraits live at /team_v2/cropped/<n>.jpg (clean square crops
// with the baked-in name/role text removed). Cache-buster v=4 forces
// browsers off the previous broken crops.
const CROPPED = (src) => `${src.replace("/team_v2/", "/team_v2/cropped/")}?v=4`;

export default function Team() {
  return (
    <div data-testid="team-page">
      <SEO
        title="Team"
        description="The team driving HidroVerse forward."
        path="/team"
      />

      {/* HERO — light theme, matches the rest of the site */}
      <section
        className="px-6 md:px-12 lg:px-20 pt-32 pb-14 md:pb-20"
        style={{ background: "#FAFAF7", color: "#0C2A40" }}
        data-testid="team-hero"
      >
        <div className="max-w-[1300px] mx-auto">
          <h1
            className="font-display"
            style={{
              fontSize: "clamp(2.6rem, 6.4vw, 5.6rem)",
              lineHeight: 1.04,
              letterSpacing: "-0.02em",
              fontWeight: 400,
              color: "#0C2A40",
            }}
          >
            Many hands.<br />
            <span style={{ color: "#1FB6C1" }}>One water company.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-[1.05rem] md:text-[1.1rem] font-light text-black/70 leading-relaxed">
            The team driving HidroVerse forward.
          </p>
        </div>
      </section>

      {/* EDITORIAL + SIDE GALLERY */}
      <section
        className="px-6 md:px-12 lg:px-20 py-14 md:py-20"
        style={{ background: "#FFFFFF", color: "#0C2A40" }}
        data-testid="team-editorial"
      >
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* LEFT — editorial copy */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <h2
              className="font-display"
              style={{
                fontSize: "clamp(1.8rem, 3.4vw, 2.8rem)",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                fontWeight: 500,
                color: "#0C2A40",
              }}
            >
              Water doesn't fail<br />
              on schedule.<br />
              <span style={{ color: "#1FB6C1" }}>Neither do we.</span>
            </h2>

            <div
              className="mt-8 space-y-5 font-light"
              style={{ fontSize: "1rem", lineHeight: 1.75, color: "#1A1F25", maxWidth: "60ch" }}
            >
              <p>
                At three in the morning, when a UV lamp drops in a lodge two
                hundred kilometres from the nearest hardware store, what matters
                isn't the brand on the equipment. It's whether someone picks up.
              </p>
              <p>
                That's why HidroVerse exists as a <strong style={{ color: "#0C2A40", fontWeight: 600 }}>team</strong>
                {" "}— not a head office that subcontracts everything that matters.
                We dispatch the right specialists for the right work — senior
                specialists run the design meetings, field technicians run the
                installs, and an after-care team owns the response when a site
                calls. Each discipline carried by people who do that thing
                every day, not by a single generalist stretched thin.
              </p>
              <p>
                We are deliberately small enough to know every install by name,
                and deliberately structured so no one ever waits for a call back.
                Internal sales, procurement, operations, technical, after-care —
                every function lives under the same roof in Nelspruit, and every
                site has a direct line through to it.
              </p>
            </div>
          </div>

          {/* RIGHT — small side gallery, sticky on desktop. Includes CEO. */}
          <aside className="lg:col-span-5 order-1 lg:order-2">
            <div className="lg:sticky lg:top-28">
              <div
                className="grid gap-x-3 md:gap-x-4 gap-y-5 md:gap-y-6"
                style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}
                data-testid="team-gallery"
              >
                {TEAM.map(([name, role, img], i) => {
                  const first = name.split(" ")[0];
                  return (
                    <div key={name} data-testid={`team-member-${i + 1}`}>
                      <div
                        className="aspect-square overflow-hidden rounded-full"
                        style={{ background: "#f0f2f2" }}
                      >
                        <img
                          src={CROPPED(img)}
                          alt={`${name} — ${role}`}
                          loading="lazy"
                          className="w-full h-full object-cover"
                          style={{ filter: "saturate(0.95)" }}
                          onError={(e) => { e.currentTarget.src = img; }}
                        />
                      </div>
                      <div className="mt-2 text-center">
                        <div
                          style={{
                            fontSize: "0.74rem",
                            fontWeight: 600,
                            color: "#0C2A40",
                            lineHeight: 1.15,
                            letterSpacing: "-0.005em",
                          }}
                        >
                          {first}
                        </div>
                        <div
                          className="font-light"
                          style={{
                            fontSize: "0.62rem",
                            color: "rgba(12,42,64,0.62)",
                            lineHeight: 1.25,
                            marginTop: 2,
                          }}
                        >
                          {role}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* CEO MESSAGE — charcoal horizontal banner (no portrait) */}
      <CEOMessage showCTA />

      {/* Closing */}
      <section className="px-6 md:px-12 lg:px-20 py-14 md:py-20" style={{ background: "#fafafa" }}>
        <div className="max-w-[1100px] mx-auto text-center">
          <h2 className="font-display" style={{ fontSize: "clamp(2rem, 4vw, 3.6rem)", lineHeight: 1.05 }}>
            Get in touch with the team.
          </h2>
          <p className="mt-6 max-w-xl mx-auto font-light text-black/70 leading-relaxed text-[1rem]">
            One number. One inbox. One signature on every install.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="cyan-pill">Contact us →</Link>
            <Link to="/about" className="ghost-pill">Read the company story</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
